// Warm pad — polyphonic saw synth, slow attack, into a reverb. Sustained
// chords via attack/release (theremin hold). Macro = volume swell (left hand).

import * as Tone from "tone";
import { degreeToNote } from "../util/scales.ts";
import type { NoteEvent, ScaleName } from "../util/types.ts";
import { clamp01, clampVelocity, type Instrument } from "./types.ts";

const BASE_OCTAVE = 3;
const DEFAULT_MACRO = 0.7;

function macroToGain(v: number): number {
  return 0.15 + 0.85 * clamp01(v);
}

export function createPad(): Instrument {
  let synth: Tone.PolySynth<Tone.Synth> | null = null;
  let reverb: Tone.Freeverb | null = null;
  let swell: Tone.Gain | null = null;
  let key = "C";
  let scale: ScaleName = "majorPentatonic";

  const resolve = (ev: NoteEvent): string | number =>
    ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, BASE_OCTAVE) : (ev.note ?? "C3");

  return {
    id: "warm-pad",
    name: "Warm pad",
    category: "pads",
    archetype: "theremin",
    scaleLocked: true,
    polyphony: "poly",
    init(output) {
      swell = new Tone.Gain(macroToGain(DEFAULT_MACRO));
      swell.connect(output);
      reverb = new Tone.Freeverb({ roomSize: 0.8, dampening: 2200, wet: 0.35 });
      reverb.connect(swell);
      synth = new Tone.PolySynth(Tone.Synth);
      synth.set({
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.6, decay: 0.4, sustain: 0.8, release: 2 },
      });
      synth.volume.value = -10;
      synth.connect(reverb);
    },
    trigger(ev, time) {
      synth?.triggerAttackRelease(resolve(ev), ev.duration ?? "2n", time, clampVelocity(ev.velocity));
    },
    attack(ev, time) {
      synth?.triggerAttack(resolve(ev), time, clampVelocity(ev.velocity));
    },
    release(ev, time) {
      synth?.triggerRelease(resolve(ev), time);
    },
    setScale(k, s) {
      key = k;
      scale = s;
    },
    setMacro(v) {
      swell?.gain.rampTo(macroToGain(v), 0.1);
    },
    dispose() {
      synth?.dispose();
      reverb?.dispose();
      swell?.dispose();
      synth = null;
      reverb = null;
      swell = null;
    },
  };
}
